import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { EventVendorRecipient, UploadLinkConfig } from '@peles/shared';
import { ComposeEmailModal } from '../messaging/ComposeEmailModal';
import { UploadLinkOptions } from '../messaging/UploadLinkOptions';
import { useSendVendorEmail } from '../../hooks/useEvents';
import { useThreadCapabilities } from '../../hooks/useThreadCapabilities';
import { fetchRecordFolders } from '../../api/threadTools';

export interface ComposeVendorEmailModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  /** Selected vendors — all must already have a resolved email. */
  recipients: EventVendorRecipient[];
}

/**
 * Compose an email to the selected event vendors. A thin wrapper over the
 * generic {@link ComposeEmailModal} that adds vendor-specific behaviour: the
 * recipients come from the vendor selection, and an optional per-vendor
 * "upload a file" link can be appended to each email.
 *
 * Where reply-by-email is configured, each vendor's letter also opens (or
 * continues) a conversation of its own on the event, and the vendor's reply
 * arrives there instead of only in the sender's inbox. On by default; it is
 * not offered where nothing would collect the reply.
 */
export function ComposeVendorEmailModal({
  open,
  onClose,
  eventId,
  recipients,
}: ComposeVendorEmailModalProps) {
  const [includeUploadLink, setIncludeUploadLink] = useState(false);
  const [uploadConfig, setUploadConfig] = useState<UploadLinkConfig>({});
  const [asConversation, setAsConversation] = useState(true);
  const [ccVendorContacts, setCcVendorContacts] = useState(true);
  const sendEmail = useSendVendorEmail(eventId);
  const { replyByEmail } = useThreadCapabilities();
  /*
   * The event's library folders — the list a conversation about the event
   * offers, from the endpoint that walks the library (Phase 8: one file
   * system). Only real folders: the event's own node is "the record's own
   * files", which is what choosing nothing already means. A vendor email
   * submits the folder's uuid.
   */
  const { data: recordFolders, isLoading: foldersLoading } = useQuery({
    queryKey: ['record-folders', 'Event', eventId],
    queryFn: () => fetchRecordFolders({ entityType: 'Event', entityId: eventId }),
    enabled: open && includeUploadLink,
  });
  const folders = useMemo(
    () =>
      (recordFolders ?? []).flatMap((folder) =>
        folder.nodeId.startsWith('folder:')
          ? [{ id: folder.nodeId.slice('folder:'.length), path: folder.path }]
          : [],
      ),
    [recordFolders],
  );

  useEffect(() => {
    if (open) {
      setIncludeUploadLink(false);
      setUploadConfig({});
      setAsConversation(true);
      setCcVendorContacts(true);
    }
  }, [open]);

  // The contacts flagged "CC on vendor emails" on each chosen vendor.
  const ccCount = recipients.reduce((sum, r) => sum + (r.ccEmails?.length ?? 0), 0);

  return (
    <ComposeEmailModal
      open={open}
      onClose={onClose}
      title="Email Vendors"
      fixedRecipients={recipients.map((r) => ({
        id: r.vendorId,
        label: r.businessName,
        email: r.email,
        // Fills a saved reply's vendor tags only while this vendor is the one
        // recipient — the composer decides, since it sees manual addresses too.
        mergeRecord: { entityType: 'Vendor', entityId: r.vendorId },
      }))}
      allowManualRecipients
      eventIdForDocuments={eventId}
      mergeRecord={{ entityType: 'Event', entityId: eventId }}
      extraOptions={
        <>
          {ccCount > 0 && (
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={ccVendorContacts}
                onChange={(e) => setCcVendorContacts(e.target.checked)}
              />
              <span>
                CC the vendors&apos; contacts ({ccCount})
                <span className="block text-xs text-gray-500">
                  Each vendor&apos;s contacts marked &ldquo;CC on vendor emails&rdquo; are copied on
                  that vendor&apos;s email:{' '}
                  {recipients
                    .flatMap((r) => r.ccEmails ?? [])
                    .slice(0, 5)
                    .join(', ')}
                  {ccCount > 5 ? ` and ${ccCount - 5} more` : ''}
                </span>
              </span>
            </label>
          )}
          {replyByEmail && (
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="mt-0.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                checked={asConversation}
                onChange={(e) => setAsConversation(e.target.checked)}
              />
              <span>
                Collect replies in a conversation per vendor
                <span className="block text-xs text-gray-500">
                  Each vendor&apos;s reply lands in its own conversation on this event. Off, replies
                  go to your inbox.
                </span>
              </span>
            </label>
          )}
          <UploadLinkOptions
            enabled={includeUploadLink}
            onEnabledChange={setIncludeUploadLink}
            config={uploadConfig}
            onConfigChange={setUploadConfig}
            // A vendor email always has a record to attach to — the vendor on
            // this event — so a folder only says where it is *also* filed.
            hasRecordTarget
            // Sent from an event, so the folders offered are that event's. The
            // whole library was the old answer, and it put a hundred other
            // clients' folders one keystroke from the right one.
            folders={folders}
            foldersLoading={foldersLoading}
          />
        </>
      }
      sendLabel={(count) => `Send to ${count} recipient${count === 1 ? '' : 's'}`}
      onSend={async (payload) => {
        const result = await sendEmail.mutateAsync({
          vendorIds: recipients.map((r) => r.vendorId),
          subject: payload.subject,
          bodyHtml: payload.bodyHtml,
          attachments: payload.attachments,
          // Forwarded, not dropped. Without this a file picked from the library
          // vanishes silently on the one screen the feature was asked for.
          fileIds: payload.fileIds,
          ccSelf: payload.ccSelf,
          ccVendorContacts: ccCount > 0 && ccVendorContacts,
          additionalEmails: payload.additionalEmails,
          includeUploadLink,
          ...(includeUploadLink ? { uploadRequest: uploadConfig } : {}),
          // Always explicit, and on only when the choice was on screen: while
          // the capability is loading, or where it failed, nothing was offered
          // and nothing is opted into.
          asConversation: replyByEmail && asConversation,
        });
        return {
          sentCount: result.sent.length,
          skipped: result.skipped.map((skip) => ({
            label:
              recipients.find((r) => r.vendorId === skip.vendorId)?.businessName ?? skip.vendorId,
            reason: skip.reason,
          })),
        };
      }}
    />
  );
}
